/**
 * @fileoverview Global right-click (context menu) blocker.
 * @description Prevents the browser's native context menu app-wide when enabled via
 * VITE_DISABLE_RIGHT_CLICK, by intercepting the `contextmenu` event on `document`. A true no-op —
 * no listener is attached at all — when the flag is off.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect } from 'react'

import { appConfig } from '@/config/app-config'

/**
 * @description Disables the browser's context menu for as long as the calling component is
 * mounted, when `appConfig.disableRightClick` is true; does nothing otherwise.
 */
export function useDisableRightClick(): void {
  useEffect(() => {
    if (!appConfig.disableRightClick) {
      return
    }

    /**
     * @description Suppresses the browser's native context menu.
     * @param {MouseEvent} event - The triggering contextmenu event.
     */
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault()
    }

    document.addEventListener('contextmenu', handleContextMenu)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [])
}
